// The CARD of a tune map: the map as text, one line a cell, for reading and
// for paper. The grid (TuneMapView) has far less to say per bar than a chart
// and leaves most of a page empty; the card says it in lines — shape,
// interval, device, chords — under section headings in chart order, the
// bars implied by the chords column (one bar a bar line). Pure: a chart and
// its map in, a card out, nothing here knows about a page. A line reads the
// interval first (the position from home), then the device, then the shape
// that sits there.
//
// Three rules shape it. The card never invents structure: it draws the
// chart's OWN repeats, endings, segno, coda and directives as full-width
// rows between the lines (a repeat sign where the chart opens and closes
// one, a volta bracket "1." / "2." where the endings begin, the sign where a
// segno or coda sits, "D.S. al Coda" where the chart says so), so a reader
// scanning down the card finds where the second time leaves the first; two
// sections written out in full print in full, in chart order, exactly as the
// chart repeats its chords (user ruling Oct 2026, replacing a fold that
// printed "as A to the run-up, then", which could not be played from). A
// pedal vamp puts its note in the section's heading ("the fifth in the
// bass") rather than on every line. And a run written "×n" prints ONCE with
// its unit where every repetition lays out alike (No Room's "i IV ×8"), or
// once per repetition where they differ (Moment's Notice's vamp: one chord a
// bar, then two a bar).

import type { IRealChart } from './ireal/types';
import {
  DEVICE_LABELS,
  type IntervalLabel,
  type TuneMap,
  type TuneMapCell,
  type TuneMapCellName,
  type TuneMapChord,
} from './tuneMap';

export interface TuneMapCardLine {
  /** Index of the cell the line prints; a split run prints several lines. */
  cell: number;
  shape: string;
  interval: IntervalLabel;
  name?: TuneMapCellName;
  approach: boolean;
  /** Written bars the line covers, inclusive. */
  bars: [number, number];
  /** The chords by bar, one entry per bar line. */
  chords: TuneMapChord[][];
}

export type TuneMapCardMarkKind =
  | 'repeat-open'
  | 'repeat-close'
  | 'ending'
  | 'segno'
  | 'coda'
  | 'directive';

/** A row of its own between the lines, drawing the chart's own structure. */
export interface TuneMapCardMark {
  kind: TuneMapCardMarkKind;
  /** The written bar the mark sits on. */
  bar: number;
  /** "1." for an ending, the directive's words; empty for a sign. */
  text: string;
}

export type TuneMapCardRow =
  | { kind: 'line'; line: TuneMapCardLine }
  | { kind: 'mark'; mark: TuneMapCardMark };

export interface TuneMapCardSection {
  /** The section's letter, or null for a stretch before the first letter
   *  (or a chart with no letters). */
  label: string | null;
  /** Written bars, inclusive. */
  bars: [number, number];
  /** The lines in chart order, with the chart's structure marks between them. */
  rows: TuneMapCardRow[];
}

/** The lines of a section, the marks left out. */
export const rowLines = (rows: TuneMapCardRow[]): TuneMapCardLine[] =>
  rows.flatMap((r) => (r.kind === 'line' ? [r.line] : []));

export interface TuneMapCard {
  sections: TuneMapCardSection[];
}

const ORDINALS: Record<string, string> = {
  '1': 'root',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
  '5': 'fifth',
  '6': 'sixth',
  '7': 'seventh',
};

/** "5" → "the fifth", "♭7" → "the flat seventh". */
export function degreeWords(degree: string): string {
  const accidental = degree.startsWith('♭') ? 'flat ' : degree.startsWith('♯') ? 'sharp ' : '';
  const number = degree.replace(/^[♭♯]/, '');
  return `the ${accidental}${ORDINALS[number] ?? number}`;
}

/**
 * The chart's structure as marks, each with its place among the lines: a
 * mark at a bar's START (a repeat opening, an ending beginning, a segno, a
 * coda sign) sits before the lines that start there, one at a bar's END (a
 * repeat closing, a directive) after the lines that start there. Keys order
 * the rows: a bar's start marks, then its lines, then its end marks, in
 * tenths so the kinds keep their order.
 */
function chartMarks(chart: IRealChart): { mark: TuneMapCardMark; key: number }[] {
  const out: { mark: TuneMapCardMark; key: number }[] = [];
  chart.measures.forEach((m, bar) => {
    const prev = chart.measures[bar - 1];
    const start = (kind: TuneMapCardMarkKind, text: string, order: number) =>
      out.push({ mark: { kind, bar, text }, key: bar + order / 10 });
    const end = (kind: TuneMapCardMarkKind, text: string, order: number) =>
      out.push({ mark: { kind, bar, text }, key: bar + 0.7 + order / 10 });
    if (m.open === 'repeat') start('repeat-open', '', 0);
    if (m.ending != null && m.ending !== prev?.ending)
      start('ending', m.ending ? `${m.ending}.` : '', 1);
    if (m.segno) start('segno', '', 2);
    if (m.coda) start('coda', '', 3);
    if (m.close === 'repeat') end('repeat-close', '', 0);
    if (m.directive) end('directive', m.directive, 1);
  });
  return out;
}

/** A line's place among the marks: after its bar's start marks. */
const lineKey = (line: TuneMapCardLine) => line.bars[0] + 0.5;

/** Chords grouped by their written bar, in order. */
function byBar(chords: TuneMapChord[]): TuneMapChord[][] {
  const bars: TuneMapChord[][] = [];
  for (const chord of chords) {
    const last = bars[bars.length - 1];
    if (last && last[0].bar === chord.bar) last.push(chord);
    else bars.push([chord]);
  }
  return bars;
}

const layoutOf = (bars: TuneMapChord[][]) =>
  bars.map((bar) => bar.map((c) => c.symbol).join(' ')).join(' | ');

/** The lines a cell prints: one, or one per repetition of a "×n" run whose
 *  repetitions lay out differently. */
function cellLines(cell: TuneMapCell, index: number): TuneMapCardLine[] {
  const base = {
    cell: index,
    interval: cell.interval,
    ...(cell.name ? { name: cell.name } : {}),
    approach: cell.approach,
  };
  const times = cell.times ?? 1;
  const events = cell.chords.reduce((n, c) => Math.max(n, c.event + 1), 0);
  if (times > 1 && events % times === 0) {
    const unit = events / times;
    const repetitions = Array.from({ length: times }, (_, r) =>
      byBar(cell.chords.filter((c) => Math.floor(c.event / unit) === r)),
    );
    const layouts = repetitions.map(layoutOf);
    if (!layouts.every((l) => l === layouts[0])) {
      const shape = cell.shape.replace(/ ×\d+$/, '');
      return repetitions.map((chords) => ({
        ...base,
        shape,
        bars: [chords[0][0].bar, chords[chords.length - 1][0].bar],
        chords,
      }));
    }
    return [{ ...base, shape: cell.shape, bars: cell.bars, chords: repetitions[0] }];
  }
  return [{ ...base, shape: cell.shape, bars: cell.bars, chords: byBar(cell.chords) }];
}

/** The chart's sections as bar ranges: a stretch before the first letter is
 *  unlettered, and a chart with no letters is one unlettered section. */
function sectionRanges(chart: IRealChart): { label: string | null; bars: [number, number] }[] {
  const n = chart.measures.length;
  const out: { label: string | null; bars: [number, number] }[] = [];
  chart.measures.forEach((m, i) => {
    if (m.section) out.push({ label: m.section, bars: [i, i] });
    else if (!out.length) out.push({ label: null, bars: [0, 0] });
    out[out.length - 1].bars[1] = i;
  });
  if (!out.length && n > 0) out.push({ label: null, bars: [0, n - 1] });
  return out;
}

export function tuneMapCard(chart: IRealChart, map: TuneMap): TuneMapCard {
  const ranges = sectionRanges(chart);
  const lines: TuneMapCardLine[][] = ranges.map(() => []);
  const sectionAt = (bar: number) => {
    let at = 0;
    ranges.forEach((r, i) => {
      if (r.bars[0] <= bar) at = i;
    });
    return at;
  };
  map.cells.forEach((cell, index) => {
    for (const line of cellLines(cell, index)) lines[sectionAt(line.bars[0])]?.push(line);
  });
  // The chart's structure, each mark in the section its bar belongs to,
  // then the rows in key order (a stable sort keeps the lines' chart order).
  const marks = chartMarks(chart);
  const sections: TuneMapCardSection[] = ranges.map((r, i) => {
    const rows: { row: TuneMapCardRow; key: number }[] = [
      ...lines[i].map((line) => ({ row: { kind: 'line' as const, line }, key: lineKey(line) })),
      ...marks
        .filter(({ mark }) => mark.bar >= r.bars[0] && mark.bar <= r.bars[1])
        .map(({ mark, key }) => ({ row: { kind: 'mark' as const, mark }, key })),
    ];
    rows.sort((a, b) => a.key - b.key);
    return { ...r, rows: rows.map((x) => x.row) };
  });
  return {
    sections,
  };
}

/** The head as one line: "Moment's Notice · John Coltrane · E♭ · A16 B14 C8 · ♩=240". */

/** A line's chords column as text: bars divided by " | ". */
export function lineChordsText(line: TuneMapCardLine): string {
  return layoutOf(line.chords);
}

/** One line as text, the four columns divided by " · ": the test and debug form. */
export function cardLineText(line: TuneMapCardLine): string {
  return [
    line.interval,
    line.name ? DEVICE_LABELS[line.name] : '',
    line.shape,
    lineChordsText(line),
  ]
    .map((s) => s || '–')
    .join(' · ');
}
